import {
  IMPORT_REPEAT_INTERVAL_DAYS,
  IMPORT_REPEAT_RRULE_CONFIG,
  RRULE_BYDAY_BY_MOMENT_WEEKDAY,
  RRULE_BYDAY_LABEL_KEYS,
  RRULE_PARTS
} from 'constants/day-plan';
import { FORMAT_ABBR_MONTH_DAY_YEAR } from 'constants/general';
import { EventSeriesRRULEFreq, ImportEventRepeat, RRULEByDay } from 'enums';
import {
  CreateEventSeriesItem,
  FileImportEventSeries
} from 'interfaces/day-plan.interface';
import moment from 'moment';
import { t } from 'services/i18n.service';
import {
  combineLocalDateTimeWithSeconds,
  parseYmdMoment
} from 'utils/time.util';

export const weekdayToByDay = (dateYmd: string): RRULEByDay => {
  const day = parseYmdMoment(dateYmd);
  if (day == null) {
    return RRULEByDay.MO;
  }
  return RRULE_BYDAY_BY_MOMENT_WEEKDAY[day.day()];
};

const repeatIntervalDays = (
  repeat: Exclude<ImportEventRepeat, ImportEventRepeat.NONE>
): number => {
  if (repeat === ImportEventRepeat.DAILY) {
    return IMPORT_REPEAT_INTERVAL_DAYS.daily;
  }
  if (repeat === ImportEventRepeat.WEEKLY) {
    return IMPORT_REPEAT_INTERVAL_DAYS.weekly;
  }
  return IMPORT_REPEAT_INTERVAL_DAYS.fortnightly;
};

/**
 * Inclusive occurrence count from first date through repeat_end_on at the
 * given frequency step. Returns 0 when the range is invalid.
 */
export const countOccurrences = ({
  repeat,
  firstDate,
  repeatEndOn
}: {
  repeat: Exclude<ImportEventRepeat, ImportEventRepeat.NONE>;
  firstDate: string;
  repeatEndOn: string;
}): number => {
  const start = parseYmdMoment(firstDate)?.startOf('day');
  const end = parseYmdMoment(repeatEndOn)?.startOf('day');
  if (start == null || end == null) {
    return 0;
  }
  if (end.isBefore(start)) {
    return 0;
  }
  const step = repeatIntervalDays(repeat);
  let count = 0;
  const cursor = start.clone();
  while (cursor.isSameOrBefore(end)) {
    count += 1;
    cursor.add(step, 'days');
  }
  return count;
};

const formatRRULEPart = (key: string, value: string | number): string =>
  `${key}=${value}`;

/** Build one unfolded RRULE line with COUNT (no UNTIL). */
export const buildEventSeriesRRULE = ({
  repeat,
  firstDate,
  repeatEndOn
}: {
  repeat: Exclude<ImportEventRepeat, ImportEventRepeat.NONE>;
  firstDate: string;
  repeatEndOn: string;
}): string | null => {
  const count = countOccurrences({ repeat, firstDate, repeatEndOn });
  if (count < 1) {
    return null;
  }
  const config = IMPORT_REPEAT_RRULE_CONFIG[repeat];
  const parts = [formatRRULEPart(RRULE_PARTS.KEY.FREQ, config.freq)];
  if (config.interval != null) {
    parts.push(formatRRULEPart(RRULE_PARTS.KEY.INTERVAL, config.interval));
  }
  if (config.includeByDay) {
    parts.push(
      formatRRULEPart(RRULE_PARTS.KEY.BYDAY, weekdayToByDay(firstDate))
    );
  }
  parts.push(formatRRULEPart(RRULE_PARTS.KEY.COUNT, count));
  return `${RRULE_PARTS.PREFIX}${parts.join(';')}`;
};

export const buildCreateEventSeriesItems = ({
  seriesRows,
  timezone
}: {
  seriesRows: FileImportEventSeries[];
  timezone: string;
}): CreateEventSeriesItem[] => {
  const items: CreateEventSeriesItem[] = [];
  seriesRows.forEach((row) => {
    const rrule = buildEventSeriesRRULE({
      repeat: row.repeat,
      firstDate: row.date,
      repeatEndOn: row.repeat_end_on
    });
    const dtstart = combineLocalDateTimeWithSeconds(row.date, row.start_time);
    const dtend = combineLocalDateTimeWithSeconds(row.date, row.end_time);
    if (rrule == null || dtstart == null || dtend == null) {
      return;
    }
    items.push({
      title: row.title.trim(),
      dtstart_local: dtstart,
      dtend_local: dtend,
      timezone,
      rrule
    });
  });
  return items;
};

interface ParsedEventSeriesRRULE {
  freq: string | null;
  interval: number;
  byDays: RRULEByDay[];
  count: number | null;
}

const DEFAULT_RRULE_INTERVAL = 1;

const isRRULEByDay = (token: string): token is RRULEByDay =>
  Object.values(RRULEByDay).includes(token as RRULEByDay);

const parseRRULEParts = (rrule: string): Record<string, string> => {
  const body = rrule.startsWith(RRULE_PARTS.PREFIX)
    ? rrule.slice(RRULE_PARTS.PREFIX.length)
    : rrule;
  const parts: Record<string, string> = {};
  body.split(';').forEach((segment) => {
    const separatorIndex = segment.indexOf('=');
    if (separatorIndex < 1) {
      return;
    }
    const key = segment.slice(0, separatorIndex);
    const value = segment.slice(separatorIndex + 1);
    if (key.length > 0 && value.length > 0) {
      parts[key] = value;
    }
  });
  return parts;
};

export const parseEventSeriesRRULE = (rrule: string): ParsedEventSeriesRRULE => {
  const parts = parseRRULEParts(rrule);
  const intervalRaw = Number(parts[RRULE_PARTS.KEY.INTERVAL]);
  const countRaw = Number(parts[RRULE_PARTS.KEY.COUNT]);
  const byDays = (parts[RRULE_PARTS.KEY.BYDAY] ?? '')
    .split(',')
    .map((token) => token.trim())
    .filter(isRRULEByDay);
  return {
    freq: parts[RRULE_PARTS.KEY.FREQ] ?? null,
    interval:
      Number.isFinite(intervalRaw) && intervalRaw > 0
        ? intervalRaw
        : DEFAULT_RRULE_INTERVAL,
    byDays,
    count: Number.isFinite(countRaw) && countRaw > 0 ? countRaw : null
  };
};

const formatByDayLabels = (byDays: RRULEByDay[]): string =>
  byDays.map((day) => t(RRULE_BYDAY_LABEL_KEYS[day])).join(', ');

const formatDailyRecurrence = (interval: number): string => {
  if (interval <= DEFAULT_RRULE_INTERVAL) {
    return t('day_plan.series_recurrence_daily');
  }
  return t('day_plan.series_recurrence_daily_interval', { count: interval });
};

const formatWeeklyRecurrence = (
  interval: number,
  byDays: RRULEByDay[]
): string => {
  if (byDays.length === 0) {
    if (interval <= DEFAULT_RRULE_INTERVAL) {
      return t('day_plan.series_recurrence_weekly_plain');
    }
    return t('day_plan.series_recurrence_weekly_interval_plain', {
      count: interval
    });
  }
  const day = formatByDayLabels(byDays);
  if (interval <= DEFAULT_RRULE_INTERVAL) {
    return t('day_plan.series_recurrence_weekly', { day });
  }
  return t('day_plan.series_recurrence_weekly_interval', {
    count: interval,
    day
  });
};

const formatRecurrenceBase = (parsed: ParsedEventSeriesRRULE): string => {
  if (parsed.freq === EventSeriesRRULEFreq.DAILY) {
    return formatDailyRecurrence(parsed.interval);
  }
  if (parsed.freq === EventSeriesRRULEFreq.WEEKLY) {
    return formatWeeklyRecurrence(parsed.interval, parsed.byDays);
  }
  return t('day_plan.series_recurrence_custom');
};

/** Plain-language recurrence line for Manage Event Series rows. */
export const formatEventSeriesRecurrenceLabel = (rrule: string): string => {
  const parsed = parseEventSeriesRRULE(rrule);
  const base = formatRecurrenceBase(parsed);
  if (parsed.count == null) {
    return base;
  }
  return `${base} · ${t('day_plan.series_occurrence_count', {
    count: parsed.count
  })}`;
};

const formatLocalClockLabel = (localDateTime: string): string => {
  const parsed = moment(localDateTime);
  return parsed.isValid() ? parsed.format('hh:mm a') : '';
};

/** Clock range from local wall times (`YYYY-MM-DDTHH:mm:ss`). */
export const formatEventSeriesTimeRangeLabel = (
  dtstartLocal: string,
  dtendLocal: string
): string => {
  const startLabel = formatLocalClockLabel(dtstartLocal);
  const endLabel = formatLocalClockLabel(dtendLocal);
  if (startLabel.length === 0) {
    return '';
  }
  return endLabel.length > 0 ? `${startLabel} – ${endLabel}` : startLabel;
};

export const formatEventSeriesStartDateLabel = (
  dtstartLocal: string
): string => {
  const parsed = moment(dtstartLocal);
  if (!parsed.isValid()) {
    return '';
  }
  return t('day_plan.series_starts', {
    date: parsed.format(FORMAT_ABBR_MONTH_DAY_YEAR)
  });
};

/** Time range + start date for Manage Event Series rows. */
export const formatEventSeriesScheduleLabel = (
  dtstartLocal: string,
  dtendLocal: string
): string => {
  const timeLabel = formatEventSeriesTimeRangeLabel(dtstartLocal, dtendLocal);
  const startLabel = formatEventSeriesStartDateLabel(dtstartLocal);
  if (timeLabel.length === 0) {
    return startLabel;
  }
  if (startLabel.length === 0) {
    return timeLabel;
  }
  return `${timeLabel} · ${startLabel}`;
};
